// src/screens/CreatorGraphsDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Screen from '../Screen';
import api from '../api';

import backIcon from '../assets/backspace-icon.png';
import infoIcon from '../assets/info-icon.png';

import LineChart from '../components/charts/LineChart';
import CreatorGraphsExplainerDrawer from '../components/popups/CreatorGraphsExplainerDrawer';
import './CreatorGraphsDetail.css';
import downIcon from '../assets/dropdown-icon.png';
import DateRangeDrawer, { labelForRange } from '../components/popups/DateRangeDrawer';

function safeNum(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

function fmt2(n) {
  const x = safeNum(n);
  return x.toFixed(2);
}

export default function CreatorGraphsDetail() {
  const navigate = useNavigate();

  const [series, setSeries] = useState([]);

const [range, setRange] = useState('30d'); // '7d' | '30d' | '90d' | 'all'
const [rangeOpen, setRangeOpen] = useState(false);

  const [mode, setMode] = useState('posts'); // now 'posts'
  const [deltas, setDeltas] = useState(null);
  const [totals, setTotals] = useState(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');



const daysFromRange = (r) => {
  if (r === '7d') return 7;
  if (r === '30d') return 30;
  if (r === '90d') return 90;
  return 'all';
};

const days = daysFromRange(range);
const rangeTitle = useMemo(() => labelForRange(range), [range]);


  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState('avgPostsPerDay');

  const [winWidth, setWinWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );

  useEffect(() => {
    const onResize = () => setWinWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const chartWidth = Math.min(1100, Math.max(320, winWidth - 48));




const fetchData = async (r) => {
  const d = daysFromRange(r);

  try {
    setLoading(true);
    setErr('');
    setRange(r);

    const res = await api.get(`/analytics/me/graphs/timeseries?days=${d}`);

    setSeries(res.data.series || []);
    setMode(res.data.mode || 'posts');
    setDeltas(res.data.deltas || null);
    setTotals(res.data.totals || null);
  } catch (e) {
    const status = e?.response?.status;
    const payload = e?.response?.data;

    if (status === 403 && payload?.code === 'ANALYTICS_NOT_ALLOWED') {
      setErr(payload?.message || 'Analytics is available on Premium and Premium+ plans.');
    } else {
      setErr(payload?.message || 'Failed to load engagement graphs.');
    }
  } finally {
    setLoading(false);
  }
};




useEffect(() => {
  fetchData('30d');
  // eslint-disable-next-line
}, []);

  const openExplain = (type) => {
    setDrawerType(type);
    setDrawerOpen(true);
  };

  const InfoBtn = ({ onClick, label }) => (
    <button
      type="button"
      onClick={onClick}
      className="cgd-infoBtn"
      aria-label={label}
      title={label}
    >
      <img src={infoIcon} alt="" className="cgd-infoIcon" />
    </button>
  );

  const compareText =
    deltas?.windowLabel ||
    (days === 'all'
      ? 'previous period'
      : `previous ${days} days`);

  const DeltaLine = ({ statKey }) => {
    const s = deltas?.[statKey];
    if (!s || deltas?.ok === false) return null;

    if (s.isNew) {
      return (
        <span className="cgd-delta">
          <span className="cgd-delta-pct cgd-delta-new">New</span>
          <span className="cgd-delta-sub"> • from {compareText}</span>
        </span>
      );
    }

    const pct = Number(s.pct);
    const isPos = Number.isFinite(pct) && pct > 0;
    const isNeg = Number.isFinite(pct) && pct < 0;

    const pctText = Number.isFinite(pct)
      ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`
      : '0.0%';

    return (
      <span className="cgd-delta">
        <span
          className={[
            'cgd-delta-pct',
            isPos ? 'cgd-delta-pos' : '',
            isNeg ? 'cgd-delta-neg' : '',
          ].join(' ')}
        >
          {pctText}
        </span>
        <span className="cgd-delta-sub"> • from {compareText}</span>
      </span>
    );
  };

  // ✅ Post-based X label
  const formatXPost = useMemo(() => {
    return (x) => String(x || '');
  }, []);

  // ✅ Build XY for post-based fields
  const buildXY = (key) =>
    (series || []).map((d) => ({
      x: d.label || `Post #${d.x || ''}`,
      y: safeNum(d[key]),
      meta: d, // keep original for tooltip
    }));

  // empty-state (no posts in window)
  const noData = !loading && !err && (!Array.isArray(series) || series.length === 0);

  return (
    <Screen>
      <div className="cgd-page">
        <div className="cgd-header">
          <img
            src={backIcon}
            alt="Back"
            className="cgd-back"
            onClick={() => navigate('/analytics', { replace: true })}
          />
          <span className="cgd-title">Engagement Graphs</span>
        </div>

        <div className="cgd-scroll">


<div className="cgd-rangeRow">
  <button
    type="button"
    className="cgd-rangeBtn"
    onClick={() => setRangeOpen(true)}
    aria-label="Change date range"
  >
    <span className="cgd-rangeText">{rangeTitle}</span>

    {downIcon ? (
      <img
        src={downIcon}
        alt=""
        className="cgd-rangeIcon"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    ) : (
      <span className="cgd-rangeCaret">▼</span>
    )}
  </button>
</div>

          {loading && (
            <div className="cgd-loader">
              <div className="cgd-spinner" />
              <div className="cgd-loadingText">Loading…</div>
            </div>
          )}

          {!loading && err && (
            <div className="cgd-errorCard">
              <div className="cgd-errorTitle">Couldn’t load</div>
              <div className="cgd-errorText">{err}</div>

<button className="cgd-retry" onClick={() => fetchData(range)}>

                Retry
              </button>
            </div>
          )}

          {noData && (
            <div className="cgd-emptyCard">
              <div className="cgd-emptyTitle">No posts in this period</div>
              <div className="cgd-emptyText">
                Post something and come back — your graphs will show performance per post.
              </div>
            </div>
          )}

          {!loading && !err && !noData && (
            <div className="cgd-rows">
              {/* ✅ Replace "Avg Posts/Day" chart with a summary card (title + delta + explainer) */}
              <div className="cgd-summaryCard">
                <div className="cgd-summaryTop">
                  <div className="cgd-summaryTitleWrap">
                    <div className="cgd-summaryTitle">Avg Posts / Day</div>
                    <div className="cgd-summarySub">
                      <DeltaLine statKey="avgPostsPerDay" />
                    </div>
                  </div>
                  <InfoBtn
                    onClick={() => openExplain('avgPostsPerDay')}
                    label="What does Avg Posts / Day mean?"
                  />
                </div>

                <div className="cgd-summaryValue">
                  {deltas?.avgPostsPerDay?.current != null
                    ? fmt2(deltas.avgPostsPerDay.current)
                    : '-'}
                </div>

                <div className="cgd-summaryHint">
                  Based on posts created in this window.
                </div>
              </div>

              {/* ✅ Per-post performance charts */}
              <LineChart
                title="Likes / Post"
                subtitle={<DeltaLine statKey="avgLikesPerPost" />}
                titleRight={
                  <InfoBtn
                    onClick={() => openExplain('avgLikesPerPost')}
                    label="What does Likes / Post mean?"
                  />
                }
                data={buildXY('likesPerPost')}
                width={chartWidth}
                height={240}
                formatX={formatXPost}
                tooltipFormat={({ x, y, meta }) => ({
                  title: `${x}${meta?.date ? ` • ${meta.date}` : ''}`,
                  lines: [`Likes: ${safeNum(y).toFixed(0)}`],
                })}
              />

              <LineChart
                title="Comments / Post"
                subtitle={<DeltaLine statKey="avgCommentsPerPost" />}
                titleRight={
                  <InfoBtn
                    onClick={() => openExplain('avgCommentsPerPost')}
                    label="What does Comments / Post mean?"
                  />
                }
                data={buildXY('commentsPerPost')}
                width={chartWidth}
                height={240}
                formatX={formatXPost}
                tooltipFormat={({ x, y, meta }) => ({
                  title: `${x}${meta?.date ? ` • ${meta.date}` : ''}`,
                  lines: [`Comments: ${safeNum(y).toFixed(0)}`],
                })}
              />

              <LineChart
                title="Engagement / Post"
                subtitle={<DeltaLine statKey="engagementDensity" />}
                titleRight={
                  <InfoBtn
                    onClick={() => openExplain('engagementDensity')}
                    label="What does Engagement / Post mean?"
                  />
                }
                data={buildXY('engagementDensity')}
                width={chartWidth}
                height={240}
                formatX={formatXPost}
                tooltipFormat={({ x, y, meta }) => ({
                  title: `${x}${meta?.date ? ` • ${meta.date}` : ''}`,
                  lines: [`Engagement: ${safeNum(y).toFixed(0)}`],
                })}
              />
            </div>
          )}
        </div>

        <CreatorGraphsExplainerDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          type={drawerType}
        />

<DateRangeDrawer
  open={rangeOpen}
  value={range}
  onClose={() => setRangeOpen(false)}
  onChange={(v) => {
    setRangeOpen(false);
    fetchData(v);
  }}
  title="Date ranges"
  includeToday={false}
  include90d={true}
/>


      </div>
    </Screen>
  );
}
